import React, { useState, forwardRef } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'

import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import { SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'

import ClientConfig from '@/modules/client/config'
import lang from '@/lang'

import IconSignaturePen from '@assets/images/icons/signature_pen.svg'
import IconPlus from '@assets/images/icons/add.svg'

import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { Title } from '@/modules/base/components/texts'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import SignatureWizard from '@/modules/signature/components/wizard/signature-wizard'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import TodoService from '@/modules/todo/todoService'
import { toast } from '@/modules/base/components/toast/toastConfig'

const { clientDetail: { jumpMarkLinks } } = ClientConfig

const useStyles = makeStyles(theme => ({
  titleClass: {
    marginBottom: 30,
  },
  description: {
    marginBottom: 30,
  },
  wrapper: {
    backgroundColor: theme.white,
    width: '100%',
    height: 'min-content',
    border: theme.border,
    borderRadius: theme.borderRadius,
    padding: 30,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  tooltip: {
    color: theme.textColor,
  },
  tooltipHeading: {
    fontSize: theme.fontSizes.default,
    fontWeight: theme.fontWeights.bold,
    marginBottom: 10,
  },
  tooltipBody: {
    fontSize: theme.fontSizes.smaller,
  },
  signatureRoot: {
    height: 'calc(100% - 85px)',
  },
  signatureDrawer: {
    overflow: 'hidden',
    height: '100%',
  },
  signatureContent: {
    padding: 30,
    borderLeft: `1px solid ${theme.borderColors.default}`,
  },
  signatureFooter: {
    borderLeft: `1px solid ${theme.borderColors.default}`,
  },
}), { name: 'ClientESigning' })

const ClientESigning = forwardRef(({ client }, ref) => {
  const classes = useStyles()
  const subscriptionInfo = useSelector(state => state.subscription.details)
  const hasSignatureModule = useCheckModuleAccess(SUBSCRIPTION_MODULES.SIGNATURES, subscriptionInfo)
  const [openDrawer, setOpenDrawer] = useState(false)

  const handleFinish = async(signatureData) => {
    try {
      await TodoService.createSignatureTodo({ ...signatureData, tenantCollaborationId: client.id })
      setOpenDrawer(false)
    } catch (error) {
      console.error(error)
      toast.showToast({ type: 'error', message: lang.clientDetail.widgets.eSigningWidget.errors.create })
    }
  }

  const signatureButton = (
    <div>
      <ButtonIconSquare
        icon={IconPlus}
        label={lang.clientDetail.widgets.eSigningWidget.requestSignature}
        onClick={() => setOpenDrawer(true)}
        disabled={!hasSignatureModule || !client.isAllowed}
      />
    </div>
  )

  const tooltipContent = (
    <div className={classes.tooltip}>
      <div className={classes.tooltipHeading}>{lang.clientDetail.widgets.eSigningWidget.headline}</div>
      <div className={classes.tooltipBody}>{lang.clientDetail.widgets.eSigningWidget.tooltip}</div>
    </div>
  )

  return (
    <div ref={ref} className={classes.wrapper} id={jumpMarkLinks.eSigning}>
      <Title icon={IconSignaturePen} className={classes.titleClass} size='small20'>{lang.clientDetail.widgets.eSigningWidget.headline}</Title>
      <div className={classes.description}>{lang.clientDetail.widgets.eSigningWidget.description}</div>
      <div className={classes.buttonContainer}>
        {hasSignatureModule
          ? signatureButton
          : (
            <RichTooltip
              content={tooltipContent}
              title={lang.clientDetail.widgets.eSigningWidget.headline}
              placement='top'
              width={300}
            >
              {signatureButton}
            </RichTooltip>
          )}
      </div>
      <SimpleDrawer
        open={openDrawer}
        className={classes.signatureDrawer}
        headline={lang.clientDetail.widgets.eSigningWidget.requestSignature}
        onClose={() => setOpenDrawer(false)}>
        <SignatureWizard
          tenantCollaborationId={client?.id}
          styles={{
            root: classes.signatureRoot,
            content: classes.signatureContent,
            footer: classes.signatureFooter,
          }}
          onFinish={handleFinish} />
      </SimpleDrawer>
    </div>
  )
})

export default ClientESigning
